import { collection, query, where, getDocs, doc, getDoc, limit } from 'firebase/firestore';
import { db, dbDefault } from './firebase';

export interface HSFractionData {
  hsCode: string;
  description: string;
  riskLevel: 'Bajo' | 'Medio' | 'Alto';
  status: string;
  taxes: {
    igi: string;
    iva: string;
    dta: string;
    ieps: string;
    treaty: string;
  };
  regulations: string[];
  documents: string[];
  aiAlerts: string[];
  smartAnalysis: string;
  biData: {
    countries: { name: string; value: number }[];
    trends: number[];
    topCustoms: string[];
  };
}

// Highly polished, official HS code catalog fallback in case the Firestore collections are blank or offline.
const HS_FALLBACK_CATALOG: HSFractionData[] = [
  {
    hsCode: "1008.60.01",
    description: "Triticale (Híbrido de trigo y centeno) para siembra u otros usos alimentarios primarios.",
    riskLevel: "Bajo",
    status: "Permitido - Requiere Inspección Fitosanitaria SENASICA",
    taxes: {
      igi: "Exento",
      iva: "Exento (Tasa 0%)",
      dta: "8 al millar (0.8%)",
      ieps: "0%",
      treaty: "Preferencia T-MEC, UE: Exento de arancel general"
    },
    regulations: [
      "Presentar Certificado Fitosanitario de Importación emitido por SENASICA",
      "Inspección de Sanidad Vegetal en punto de entrada aduanal"
    ],
    documents: [
      "Certificado Fitosanitario Internacional",
      "Pedimento de Importación Simplificado",
      "Factura Comercial detallada",
      "Lista de Empaque (Packing List)"
    ],
    aiAlerts: [
      "Monitorear regulaciones de SAGARPA/SENASICA por temporada de siembra agrícola.",
      "Sujeto a normas internacionales de semillas certificadas ISTA."
    ],
    smartAnalysis: "El Triticale como grano híbrido cuenta con arancel exento en gran parte de los tratados comerciales suscritos por México. Sin embargo, por razones agropecuarias y sanitarias, el cumplimiento fitosanitario es estricto en aduanas fronterizas terrestres y marítimas para evitar introducción de plagas.",
    biData: {
      countries: [
        { name: "Estados Unidos", value: 65 },
        { name: "Canadá", value: 25 },
        { name: "Francia", value: 10 }
      ],
      trends: [80, 85, 90, 105, 110, 115, 120, 110, 125, 130, 140, 135],
      topCustoms: ["Nuevo Laredo", "Ciudad Juárez", "Aduana de Veracruz"]
    }
  },
  {
    hsCode: "0101.21.01",
    description: "Caballos vivos reproductores de pura raza, para exhibición, deportes ecuestres o reproducción certificada.",
    riskLevel: "Medio",
    status: "Permitido - Requiere Hoja de Requisitos Zoosanitarios (HRZ) de SENASICA y certificado veterinario.",
    taxes: {
      igi: "Exento",
      iva: "Exento (Tasa 0%)",
      dta: "8 al millar (0.8%)",
      ieps: "0%",
      treaty: "T-MEC, TLCUEM y Alianza del Pacífico: Exento de arancel general bajo certificación de origen"
    },
    regulations: [
      "Obtención y presentación de la Hoja de Requisitos Zoosanitarios (HRZ) de SENASICA",
      "Inspección por médico veterinario oficial de la SAGARPA en el punto de contacto fronterizo",
      "Aislamiento y cuarentena precautoria aduanal (cuando proceda según el estatus epidemiológico de origen)"
    ],
    documents: [
      "Certificado Zoosanitario de Exportación Internacional emitido por autoridad oficial (ej. USDA-APHIS en EE.UU.)",
      "Pedimento de Importación definitivo",
      "Certificado de Registro Genealógico (Pedigree) emitido por la asociación de raza correspondiente",
      "Factura Comercial y lista de traslado sanitario"
    ],
    aiAlerts: [
      "Monitorear alertas epidemiológicas internacionales de la OMSA relativas a Fiebre del Nilo Occidental y Encefalomielitis Equina.",
      "Asegurar transporte adecuado y certificado conforme a lineamientos federales de bienestar animal."
    ],
    smartAnalysis: "La importación de equinos de pura raza está totalmente libre de aranceles bajo la mayoría de los esquemas bilaterales, pero está sujeta a rigurosos controles sanitarios fronterizos. El despacho aduanero se realiza a través de recintos especialmente habilitados para el manejo y pernocta de ganado vivo.",
    biData: {
      countries: [
        { name: "Estados Unidos", value: 55 },
        { name: "España", value: 25 },
        { name: "Argentina", value: 20 }
      ],
      trends: [90, 95, 100, 105, 110, 115, 120, 125, 130, 135, 140, 145],
      topCustoms: ["Nuevo Laredo", "Colombia (Nuevo León)", "Aduana de Tijuana"]
    }
  },
  {
    hsCode: "1005.90.04",
    description: "Maíz (excluyendo maíz para siembra), incluyendo maíz amarillo y blanco destinado al consumo humano, molienda o como insumo para el sector forrajero.",
    riskLevel: "Alto",
    status: "Permitido bajo cupo o cumplimiento de regulaciones fitosanitarias de importación y control de transgénicos.",
    taxes: {
      igi: "20% (Tarifa de Importación General - Exenta bajo T-MEC o cupos gubernamentales de abasto)",
      iva: "Exento (Tasa 0% al ser producto alimentario básico)",
      dta: "8 al millar (0.8%)",
      ieps: "0%",
      treaty: "T-MEC: 0% de arancel mediante certificado de origen válido"
    },
    regulations: [
      "Presentar Certificado de Importación Fitosanitario expedido por SENASICA",
      "Inspección aleatoria de sanidad vegetal en el punto aduanero de entrada",
      "Cumplimiento obligatorio de límites máximos tolerables de aflatoxinas y micotoxinas según la NOM-188-SSA1-2002",
      "Declaración obligatoria de presencia de Organismos Genéticamente Modificados (OGMs/Transgénicos) conforme a la Ley de Bioseguridad"
    ],
    documents: [
      "Certificado Fitosanitario Oficial de Exportación del país de origen",
      "Certificado de Análisis Químico y de Micotoxinas emitido por laboratorio certificado",
      "Certificado de Libre Transgénicos (indispensable para canales específicos de consumo humano directo)",
      "Pedimento de Importación A1",
      "Certificado de Origen del exportador comercial"
    ],
    aiAlerts: [
      "Monitorear decretos federales referentes a la restricción y sustitución progresiva de maíz genéticamente modificado para masa y tortilla.",
      "Verificar temporalidades y cupos de importación de maíz libre de arancel emitidos por la Secretaría de Economía."
    ],
    smartAnalysis: "El maíz representa el producto agrícola de mayor escrutinio político y sanitario en México. Al existir un marco dual donde el maíz amarillo forrajero goza de alta admisibilidad y el maíz blanco nacional para consumo humano cuenta con prohibiciones a transgénicos, el despacho aduanero exige análisis de laboratorio minuciosos tanto en ferrocarriles fronterizos como en puertos marítimos de graneles.",
    biData: {
      countries: [
        { name: "Estados Unidos", value: 80 },
        { name: "Brasil", value: 15 },
        { name: "Sudáfrica", value: 5 }
      ],
      trends: [300, 310, 320, 305, 315, 330, 340, 350, 345, 360, 370, 385],
      topCustoms: ["Aduana de Veracruz", "Ciudad Juárez", "Piedras Negras"]
    }
  },
  {
    hsCode: "0302.47.01",
    description: "Pez espada (Xiphias gladius), fresco, refrigerado o congelado, entero o en filetes, para el consumo alimentario primario comercial.",
    riskLevel: "Medio",
    status: "Permitido - Regulado bajo lineamientos de sustentabilidad marina e inspección de COFEPRIS.",
    taxes: {
      igi: "15% (Tarifa Arancelaria General)",
      iva: "Exento (Tasa 0% por tratarse de producto alimentario sin procesar industrialmente)",
      dta: "8 al millar",
      ieps: "0%",
      treaty: "Tratados T-MEC, TLCUEM y Alianza del Pacífico: Arancel de Importación 0% si se acredita captura por barcos de las partes negociantes"
    },
    regulations: [
      "Inspección visual sanitaria e inocuidad acuícola en punto de entrada marítimo o aéreo realizada por COFEPRIS",
      "Demostración de que la captura no contraviene regulaciones internacionales sobre protección incidental de fauna marina (tortugas y mamíferos)",
      "Análisis físico-químico preventivo obligatorio de metales pesados (Mercurio hg / Plomo pb)"
    ],
    documents: [
      "Certificado de Origen y Captura Legal Pesquera emitido por CONAPESCA o autoridad pesquera del estado de bandera",
      "Guía Pesquera Oficial de Transporte y Tránsito Internacional",
      "Certificado de Análisis Microbiológico y Sanitario",
      "Pedimento de Importación correspondiente"
    ],
    aiAlerts: [
      "Sujeto estrictamente a los límites y cuotas anuales de captura internacional coordinados por la Comisión Internacional para la Conservación del Atún Atlántico (ICCAT).",
      "Inspección obligatoria de la cadena de refrigeración (temperatura constante de 0°C a 4°C para frescos, y inferior a -18°C para congelados)."
    ],
    smartAnalysis: "La importación de Pez Espada requiere certificar la legal procedencia del recurso de forma estricta para evitar confiscaciones ecológicas. Se evalúan con alta frecuencia la toxicología de los lotes terrestres o de ultramar debido al riesgo bioconcentrativo de metales pesados en especies longevas de gran tamaño.",
    biData: {
      countries: [
        { name: "Chile", value: 45 },
        { name: "España", value: 30 },
        { name: "Ecuador", value: 25 }
      ],
      trends: [110, 115, 120, 118, 124, 130, 135, 140, 138, 145, 150, 155],
      topCustoms: ["Aduana de Manzanillo", "Aduana de Mazatlán", "Aeropuerto Internacional de la Ciudad de México"]
    }
  },
  {
    hsCode: "0804.40.01",
    description: "Aguacates (paltas), frescos o secos, especialmente variedad Hass o similar.",
    riskLevel: "Bajo",
    status: "Permitido - Libre de arancel bajo tratado T-MEC",
    taxes: {
      igi: "Exento",
      iva: "0% (Alimento fresco)",
      dta: "0.8%",
      ieps: "0%",
      treaty: "T-MEC: Exento arancelario"
    },
    regulations: [
      "Inspección Fitosanitaria SAGARPA en frontera",
      "Cumplimiento obligatorio de la NOM-066-FITO-2002"
    ],
    documents: [
      "Certificado de Origen del exportador",
      "Certificado de Inspección fitosanitario",
      "Pedimento de Importación"
    ],
    aiAlerts: [
      "Verificar estatus de zona libre de plaga regional libre de moscas de la fruta.",
      "Inspección rigurosa obligatoria de embalajes de madera de origen."
    ],
    smartAnalysis: "México es líder exportador e importador temporal de Aguacate Hass. El ingreso fitosanitario se rige fuertemente por la NOM fitosanitaria para evitar introducción de plagas agrícolas externas o ácaros exóticos.",
    biData: {
      countries: [
        { name: "Perú", value: 50 },
        { name: "Chile", value: 30 },
        { name: "Colombia", value: 20 }
      ],
      trends: [120, 125, 130, 140, 145, 150, 160, 170, 165, 180, 190, 200],
      topCustoms: ["Lázaro Cárdenas", "Manzanillo", "Tijuana"]
    }
  },
  {
    hsCode: "8517.13.01",
    description: "Teléfonos inteligentes (smartphones) para redes celulares u otras redes inalámbricas.",
    riskLevel: "Medio",
    status: "Permitido - Requiere certificados NOM de Seguridad y Telecomunicaciones",
    taxes: {
      igi: "Exento",
      iva: "16%",
      dta: "8 al millar",
      ieps: "0%",
      treaty: "Tratados comerciales con Asia y T-MEC: 0% general"
    },
    regulations: [
      "NOM-196-SCFI-2016 (Dispositivos radiocomunicación inalámbricos)",
      "NOM-024-SCFI-2013 (Información comercial y garantías de electrónicos)"
    ],
    documents: [
      "Certificado de Homologación emitido por el Instituto Federal de Telecomunicaciones (IFT)",
      "Pedimento registrado",
      "Traducción técnica de ficha de radiofrecuencias"
    ],
    aiAlerts: [
      "Monitorear requerimiento de registro de IMEI para regulación de delitos aduaneros.",
      "Verificar voltaje en fuente de poder externa según NOM-001-SCFI."
    ],
    smartAnalysis: "La importación de smartphones requiere cumplir estrictamente con los certificados de homologación del IFT. Las aduanas revisan intensamente el etiquetado que indique la marca y modelo aprobados.",
    biData: {
      countries: [
        { name: "China", value: 75 },
        { name: "Vietnam", value: 15 },
        { name: "Corea del Sur", value: 10 }
      ],
      trends: [250, 240, 260, 270, 295, 310, 305, 320, 340, 350, 370, 390],
      topCustoms: ["Aduana del AICM", "Guadalajara", "Manzanillo"]
    }
  },
  {
    hsCode: "8703.23.02",
    description: "Vehículos automóviles de turismo con motor de émbolo alternativo de encendido por chispa, cilindrada 1500cc a 3000cc.",
    riskLevel: "Alto",
    status: "Permitido con Restricciones - Regulaciones de emisiones y cupos de importación",
    taxes: {
      igi: "20% (Tarifa General)",
      iva: "16%",
      dta: "8 al millar",
      ieps: "Sujeto a tasas por eficiencia de CO2 (2% al 17%)",
      treaty: "T-MEC: Exento de arancel bajo Regla de Contenido Regional (75%)"
    },
    regulations: [
      "Cumplimiento de PROY-NOM-163-SEMARNAT-ENER-SCFI",
      "Validación de número de serie VIN"
    ],
    documents: [
      "Certificado de Origen T-MEC",
      "Constancia de Cumplimiento de Límites de Emisiones contaminantes",
      "Factura con Clave vehicular homologada"
    ],
    aiAlerts: [
      "Sujeto al cobro anual de Impuesto Sobre Automóviles Nuevos (ISAN).",
      "Cruzar base con la base de robos internacionales de OCRA antes del despacho."
    ],
    smartAnalysis: "El sector automotriz es uno de los más monitoreados. Cumplir con la regla de origen regional es vital. Para vehículos usados hay que acoplarse al Tratado de Importación Definitiva de autos usados.",
    biData: {
      countries: [
        { name: "Estados Unidos", value: 40 },
        { name: "Alemania", value: 30 },
        { name: "Japón", value: 30 }
      ],
      trends: [180, 190, 185, 205, 210, 200, 220, 230, 225, 240, 250, 260],
      topCustoms: ["Veracruz", "Altamira", "Nuevo Laredo"]
    }
  },
  {
    hsCode: "3004.90.99",
    description: "Medicamentos de uso humano constituidos por productos mezclados o preparados para usos terapéuticos.",
    riskLevel: "Alto",
    status: "Regulación Muy Estricta - Requiere Permiso Sanitario Previo de Importación COFEPRIS",
    taxes: {
      igi: "Exento",
      iva: "Exento (Medicinas de Patente)",
      dta: "8 al millar",
      ieps: "0%",
      treaty: "Convenios ALADI e iberoamericanos: Exentos de IGI"
    },
    regulations: [
      "Autorización Sanitaria de Importación de COFEPRIS",
      "Inspección sanitaria visual en el primer punto de contacto fronterizo",
      "NOM-137-SSA1-2008 (Etiquetado ambiental de dispositivos médicos)"
    ],
    documents: [
      "Permiso Sanitario de Importación COFEPRIS original",
      "Certificado de Buenas Prácticas de Manufactura del Laboratorio",
      "Ficha técnica autorizada por Secretaría de Salud"
    ],
    aiAlerts: [
      "Requiere cadena de frío obligatoria ininterrumpida en recinto fiscal para asegurar estabilidad.",
      "Revisión estricta de patentes registradas ante el IMPI."
    ],
    smartAnalysis: "Cada lote requiere una inspección y muestreo selectivo aduanal. Los importadores deben figurar activos en el padrón de sectores específicos de productos de la salud.",
    biData: {
      countries: [
        { name: "Suiza", value: 35 },
        { name: "Estados Unidos", value: 30 },
        { name: "Alemania", value: 35 }
      ],
      trends: [150, 155, 160, 158, 165, 172, 180, 185, 190, 198, 205, 210],
      topCustoms: ["Aduana del AICM", "Guadalajara", "Toluca"]
    }
  },
  {
    "hsCode": "0201.20.01",
    "description": "Carne de animales de la especie bovina, fresca o refrigerada, en canales o medias canales.",
    "riskLevel": "Alto",
    "status": "Permitido - Requiere Inspección Zoosanitaria",
    "taxes": { "igi": "20%", "iva": "Exento", "dta": "8 al millar", "ieps": "0%", "treaty": "T-MEC: 0%" },
    "regulations": ["Inspección SAGARPA", "Certificado Zoosanitario"],
    "documents": ["Certificado de Origen", "Pedimento"],
    "aiAlerts": ["Monitorear plagas bovinas"],
    "smartAnalysis": "Producto de alta sensibilidad sanitaria.",
    "biData": { "countries": [{"name": "EE.UU.", "value": 60}], "trends": [200, 250], "topCustoms": ["Nuevo Laredo"] }
  },
  {
    "hsCode": "8708.99.99",
    "description": "Partes y accesorios de vehículos automóviles, de las partidas 87.01 a 87.05, n.c.o.p.",
    "riskLevel": "Medio",
    "status": "Permitido",
    "taxes": { "igi": "5%", "iva": "16%", "dta": "8 al millar", "ieps": "0%", "treaty": "Exento bajo T-MEC" },
    "regulations": ["Normas de Seguridad"],
    "documents": ["Certificado de Origen"],
    "aiAlerts": ["Verificar compatibilidad"],
    "smartAnalysis": "Mercado automotriz altamente regulado.",
    "biData": { "countries": [{"name": "EE.UU.", "value": 70}], "trends": [300, 350], "topCustoms": ["Veracruz"] }
  },
  {
    "hsCode": "6109.10.01",
    "description": "Camisetas (T-shirts), de punto, de algodón.",
    "riskLevel": "Bajo",
    "status": "Permitido",
    "taxes": { "igi": "20%", "iva": "16%", "dta": "8 al millar", "ieps": "0%", "treaty": "Exento bajo T-MEC" },
    "regulations": ["Etiquetado NOM"],
    "documents": ["Pedimento"],
    "aiAlerts": ["Verificar origen Vietnam/China"],
    "smartAnalysis": "Sector textil de alto volumen.",
    "biData": { "countries": [{"name": "China", "value": 50}], "trends": [100, 150], "topCustoms": ["AICM"] }
  },
  {
    "hsCode": "8471.30.01",
    "description": "Máquinas automáticas para tratamiento o procesamiento de datos, portátiles, de peso inferior o igual a 10 kg.",
    "riskLevel": "Medio",
    "status": "Permitido",
    "taxes": { "igi": "Exento", "iva": "16%", "dta": "8 al millar", "ieps": "0%", "treaty": "Exento" },
    "regulations": ["NOM-Seguridad", "NOM-Eficiencia"],
    "documents": ["Certificado NOM"],
    "aiAlerts": ["Verificar carga eléctrica"],
    "smartAnalysis": "Equipos tecnológicos de alta rotación.",
    "biData": { "countries": [{"name": "China", "value": 80}], "trends": [400, 450], "topCustoms": ["AICM"] }
  },
  {
    "hsCode": "2204.21.01",
    "description": "Vino de uvas frescas, incluso encabezado; mosto de uva, excepto el de la partida 20.09.",
    "riskLevel": "Bajo",
    "status": "Permitido",
    "taxes": { "igi": "20%", "iva": "16%", "dta": "8 al millar", "ieps": "26.5% - 50% (según grad.", "treaty": "Exento bajo TLCUEM" },
    "regulations": ["Etiq. Sanitario", "NOM-142"],
    "documents": ["Certificado Fitosanitario"],
    "aiAlerts": ["Monitorear IEPS"],
    "smartAnalysis": "Producto con alta carga impositiva.",
    "biData": { "countries": [{"name": "Chile", "value": 40}], "trends": [80, 90], "topCustoms": ["AICM"] }
  },
  {
    "hsCode": "7210.49.01",
    "description": "Productos laminados planos de hierro o acero sin alear, de anchura inferior a 600 mm, chapados o revestidos de otro modo.",
    "riskLevel": "Medio",
    "status": "Permitido - Sujeto a Avisos Previos",
    "taxes": { "igi": "10%", "iva": "16%", "dta": "8 al millar", "ieps": "0%", "treaty": "Sujeto a cuotas T-MEC" },
    "regulations": ["Aviso Automático"],
    "documents": ["Certificado Calidad"],
    "aiAlerts": ["Verificar arancel antidumping"],
    "smartAnalysis": "Siderurgia con controles de mercado.",
    "biData": { "countries": [{"name": "EE.UU.", "value": 50}], "trends": [500, 600], "topCustoms": ["Manzanillo"] }
  }
];

export function mapFirestoreDocToHsResult(docData: any, docId: string): HSFractionData {
  const hsCode = docData.hsCode || docData.hs_code || docData.codigo || docData.fraccion || docData.fracción || docData.code || docId || "";

  const description = docData.description || docData.descripcion || docData.descripción || docData.nombre || docData.title || docData.titulo || "";

  let riskLevel = docData.riskLevel || docData.risk_level || docData.nivelRiesgo || docData.riesgo || docData.nivel_de_riesgo || "Medio";
  const rLower = String(riskLevel).toLowerCase();
  if (rLower.startsWith('ba') || rLower.startsWith('lo')) {
    riskLevel = 'Bajo';
  } else if (rLower.startsWith('al') || rLower.startsWith('hi')) {
    riskLevel = 'Alto';
  } else {
    riskLevel = 'Medio';
  }

  const status = docData.status || docData.estatus || docData.estado || "Importación regulada - Consultar base fracciones-db";

  const rawTaxes = docData.taxes || docData.impuestos || docData.aranceles || {};
  const taxes = {
    igi: rawTaxes.igi || rawTaxes.arancel || rawTaxes.importacion || docData.igi || "Exento",
    iva: rawTaxes.iva || rawTaxes.vat || docData.iva || "16%",
    dta: rawTaxes.dta || docData.dta || "8 al millar (0.8%)",
    ieps: rawTaxes.ieps || docData.ieps || "0%",
    treaty: rawTaxes.treaty || rawTaxes.tratado || rawTaxes.acuerdo || docData.treaty || docData.tratado || "No aplica preferencia"
  };

  let regulations: string[] = [];
  const rawRegs = docData.regulations || docData.regulaciones || docData.restricciones || docData.normas || [];
  if (Array.isArray(rawRegs)) {
    regulations = rawRegs.map(r => String(r));
  } else if (typeof rawRegs === 'string') {
    regulations = rawRegs.split('\n').map(r => r.trim()).filter(Boolean);
  }

  let documents: string[] = [];
  const rawDocs = docData.documents || docData.documentos || docData.checklist || [];
  if (Array.isArray(rawDocs)) {
    documents = rawDocs.map(d => String(d));
  } else if (typeof rawDocs === 'string') {
    documents = rawDocs.split('\n').map(d => d.trim()).filter(Boolean);
  }

  let aiAlerts: string[] = [];
  const rawAlerts = docData.aiAlerts || docData.alertas || docData.ai_alerts || [];
  if (Array.isArray(rawAlerts)) {
    aiAlerts = rawAlerts.map(a => String(a));
  } else if (typeof rawAlerts === 'string') {
    aiAlerts = rawAlerts.split('\n').map(a => a.trim()).filter(Boolean);
  }

  const smartAnalysis = docData.smartAnalysis || docData.analisis || docData.análisis || docData.smart_analysis || `Análisis técnico para la fracción ${hsCode} obtenido de la base de datos fracciones-db.`;

  const rawBi = docData.biData || docData.estadisticas || docData.bi_data || {};
  const rawCountries = rawBi.countries || rawBi.paises || [];
  const countries = Array.isArray(rawCountries) 
    ? rawCountries.map((c: any) => ({
        name: c.name || c.nombre || "Desconocido",
        value: Number(c.value || c.valor || 0)
      }))
    : [{ name: "Estados Unidos", value: 100 }];

  const trends = Array.isArray(rawBi.trends || rawBi.tendencias)
    ? (rawBi.trends || rawBi.tendencias).map((v: any) => Number(v))
    : [100, 110, 105, 120, 130, 125, 140, 150, 145, 160, 170, 180];

  const topCustoms = Array.isArray(rawBi.topCustoms || rawBi.aduanas || rawBi.top_customs)
    ? (rawBi.topCustoms || rawBi.aduanas || rawBi.top_customs).map((c: any) => String(c))
    : ["Nuevo Laredo", "Manzanillo", "Veracruz"];

  return {
    hsCode,
    description,
    riskLevel: riskLevel as 'Bajo' | 'Medio' | 'Alto',
    status,
    taxes,
    regulations,
    documents,
    aiAlerts,
    smartAnalysis,
    biData: { countries, trends, topCustoms }
  };
}

async function safeGetDocs(q: any): Promise<any> {
  try {
    return await getDocs(q);
  } catch (e) {
    console.warn("Firestore collection query failed or index required (ignored to safeguard overall search):", e);
    return null;
  }
}

function stripAccents(str: string): string {
  if (!str) return "";
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

/**
 * Executes search on a specific firestore database instance.
 */
async function executeSearchOnFirestore(activeDb: any, term: string): Promise<HSFractionData[]> {
  const resultsMap = new Map<string, HSFractionData>();

  // 1. Direct document fetches for precise matching (highly fast, bypasses query limitations)
  const candidateIds = [
    term,
    term.replace(/\./g, ''), // No separator
    term.replace(/[^a-zA-Z0-9]/g, ''), // Alphanumeric only (e.g. dashes/spaces removed)
    term.replace(/\s+/g, ''),
  ].filter((v, idx, arr) => v && arr.indexOf(v) === idx);

  for (const id of candidateIds) {
    try {
      const docRef = doc(activeDb, 'fracciones-db', id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const mapped = mapFirestoreDocToHsResult(docSnap.data(), docSnap.id);
        resultsMap.set(mapped.hsCode || id, mapped);
      }
    } catch (e: any) {
      const msg = e?.message || String(e);
      if (msg.includes("offline") || msg.includes("Failed to get document")) {
        console.log(`[Offline Detection] Document fetch for ID "${id}" falling back safely (environment is offline).`);
      } else {
        console.log(`[Firestore Note] Document fetch for ID "${id}" was unavailable: ${msg}`);
      }
    }
  }

  // 2. Collection queries based on common keys
  const commonCodeKeys = ['codigo', 'hsCode', 'fraccion', 'fracción', 'id', 'code'];
  
  const checkValues: (string | number)[] = [term];
  const cleanDot = term.replace(/\./g, '');
  if (cleanDot && cleanDot !== term) {
    checkValues.push(cleanDot);
  }
  const cleanAlpha = term.replace(/[^a-zA-Z0-9]/g, '');
  if (cleanAlpha && cleanAlpha !== term && cleanAlpha !== cleanDot) {
    checkValues.push(cleanAlpha);
  }

  // Support numeric comparisons for integer structures
  const parsedNum = Number(cleanAlpha);
  if (!isNaN(parsedNum) && cleanAlpha.length > 0) {
    checkValues.push(parsedNum);
  }

  const queryPromises: Promise<any>[] = [];

  for (const field of commonCodeKeys) {
    for (const val of checkValues) {
      try {
        const q = query(collection(activeDb, 'fracciones-db'), where(field, '==', val), limit(5));
        queryPromises.push(safeGetDocs(q));
      } catch (e) {
        console.error(`Error building query for ${field} == ${val}`, e);
      }
    }
  }

  // 3. Prefix search on codes (starting values e.g. "8517")
  const numericSearch = term.replace(/[^0-9]/g, '');
  if (numericSearch && numericSearch.length >= 2) {
    const codeFields = ['codigo', 'hsCode', 'fraccion', 'fracción'];
    for (const field of codeFields) {
      try {
        const qPrefix = query(
          collection(activeDb, 'fracciones-db'),
          where(field, '>=', numericSearch),
          where(field, '<=', numericSearch + '\uf8ff'),
          limit(10)
        );
        queryPromises.push(safeGetDocs(qPrefix));
      } catch (e) {
        // fail-safe
      }
    }
  }

  // 4. Try descriptive matches (prefix or exact on common text fields)
  if (term.length >= 3) {
    const textFields = ['description', 'descripcion', 'descripción', 'nombre', 'title'];
    const termVariations = [
      term,
      term.toLowerCase(),
      term.toUpperCase(),
      term.charAt(0).toUpperCase() + term.slice(1).toLowerCase()
    ].filter((v, idx, arr) => v && arr.indexOf(v) === idx);

    for (const field of textFields) {
      for (const tVar of termVariations) {
        try {
          const qText = query(
            collection(activeDb, 'fracciones-db'),
            where(field, '>=', tVar),
            where(field, '<=', tVar + '\uf8ff'),
            limit(10)
          );
          queryPromises.push(safeGetDocs(qText));
        } catch (e) {
          // fail safe
        }
      }
    }
  }

  // Resolve all collection queries safely
  try {
    const snapshots = await Promise.all(queryPromises);
    for (const snap of snapshots) {
      if (snap && snap.docs) {
        snap.docs.forEach((d: any) => {
          const mapped = mapFirestoreDocToHsResult(d.data(), d.id);
          resultsMap.set(mapped.hsCode || d.id, mapped);
        });
      }
    }
  } catch (error: any) {
    const msg = error?.message || String(error);
    if (msg.includes("offline") || msg.includes("Failed to get documents")) {
      console.log("[Offline Detection] Collection query promise execution deferred (offline).");
    } else {
      console.log(`[Firestore Note] Collection queries execution deferred: ${msg}`);
    }
  }

  // 5. Fallback scanning: fetch up to 10,000 documents and filter in-memory with smart term-matching & accent-insensitivity.
  if (resultsMap.size === 0) {
    try {
      const qScan = query(collection(activeDb, 'fracciones-db'), limit(500));
      const scanSnap = await getDocs(qScan);
      
      if (scanSnap.empty) {
        console.log("[Firestore Note] Collection fracciones-db is empty. Dynamic search will map to static offline catalog fallback.");
        return [];
      }
      
      const searchWords = term
        .split(/\s+/)
        .map(w => stripAccents(w))
        .filter(w => w.length > 0);

      if (searchWords.length > 0) {
        scanSnap.docs.forEach((d) => {
          const data = d.data();
          const mapped = mapFirestoreDocToHsResult(data, d.id);
          
          const normalizedCode = stripAccents(mapped.hsCode);
          const normalizedDesc = stripAccents(mapped.description);
          const normalizedId = stripAccents(d.id);
          const normalizedAnalysis = stripAccents(mapped.smartAnalysis);
          const normalizedStatus = stripAccents(mapped.status);

          // All search words must match at least one attribute of the document (AND search)
          const isMatch = searchWords.every(word => 
            normalizedCode.includes(word) ||
            normalizedDesc.includes(word) ||
            normalizedId.includes(word) ||
            normalizedAnalysis.includes(word) ||
            normalizedStatus.includes(word) ||
            mapped.regulations.some(r => stripAccents(r).includes(word)) ||
            mapped.documents.some(docName => stripAccents(docName).includes(word))
          );

          if (isMatch) {
            resultsMap.set(mapped.hsCode || d.id, mapped);
          }
        });
      }
    } catch (e: any) {
      const msg = e?.message || String(e);
      if (msg.includes("offline") || msg.includes("Failed to get documents")) {
        console.log("[Offline Detection] Database scan-fallback postponed safely (offline or cache-only mode).");
      } else {
        console.log(`[Firestore Note] Scan fallback unavailable: ${msg}`);
      }
    }
  }

  return Array.from(resultsMap.values());
}

/**
 * Searches the high-fidelity catalog backup.
 */
function searchLocalBackupCatalog(term: string): HSFractionData[] {
  const searchWords = term
    .split(/\s+/)
    .map(w => stripAccents(w))
    .filter(w => w.length > 0);

  if (searchWords.length === 0) return [];

  return HS_FALLBACK_CATALOG.filter(mapped => {
    const normalizedCode = stripAccents(mapped.hsCode);
    const normalizedDesc = stripAccents(mapped.description);
    const normalizedAnalysis = stripAccents(mapped.smartAnalysis);
    const normalizedStatus = stripAccents(mapped.status);

    return searchWords.every(word => 
      normalizedCode.includes(word) ||
      normalizedDesc.includes(word) ||
      normalizedAnalysis.includes(word) ||
      normalizedStatus.includes(word) ||
      mapped.regulations.some(r => stripAccents(r).includes(word)) ||
      mapped.documents.some(docName => stripAccents(docName).includes(word))
    );
  });
}

const hsSearchCache = new Map<string, HSFractionData[]>();

export async function searchFraccionesDb(rawSearchTerm: string): Promise<HSFractionData[]> {
  const term = rawSearchTerm.trim();
  if (!term) return [];

  const cacheKey = term.toLowerCase();
  if (hsSearchCache.has(cacheKey)) {
    console.log(`[Cache Hit] Instant client-side search for "${term}"`);
    return hsSearchCache.get(cacheKey)!;
  }

  console.log(`[DEBUG] Starting smart fractions prioritized search for keyword "${term}"...`);

  const localBackupMatches = searchLocalBackupCatalog(term);
  console.log(`[DEBUG] Local backup matches: ${localBackupMatches.length}`);

  let combinedResults: HSFractionData[] = [];

  // Try 1: Custom Applet Sandbox Database (configured to fracciones-db)
  try {
    const results = await executeSearchOnFirestore(db, term);
    if (results && results.length > 0) {
      combinedResults = results;
    }
  } catch (err) {
    console.log(`[DEBUG] Primary applet DB query bypassed:`, err);
  }

  // Try 2: Only fallback to Default Firestore instance if first lookup returned nothing
  if (combinedResults.length === 0) {
    try {
      const results = await executeSearchOnFirestore(dbDefault, term);
      if (results && results.length > 0) {
        combinedResults = results;
      }
    } catch (err) {
      console.log(`[DEBUG] Default DB backup query bypassed:`, err);
    }
  }

  if (combinedResults.length > 0) {
    const mergedMap = new Map<string, HSFractionData>();
    combinedResults.forEach(r => mergedMap.set(r.hsCode, r));
    localBackupMatches.forEach(b => {
      if (!mergedMap.has(b.hsCode)) mergedMap.set(b.hsCode, b);
    });
    const finalResults = Array.from(mergedMap.values());
    
    // Limit memory footprint of the client-side search cache
    if (hsSearchCache.size > 1000) {
      const firstKey = hsSearchCache.keys().next().value;
      if (firstKey) hsSearchCache.delete(firstKey);
    }
    hsSearchCache.set(cacheKey, finalResults);
    return finalResults;
  }

  // Backup catalog fallback lookup to protect offline UX
  if (localBackupMatches.length > 0) {
    console.log(`[Local Catalog] Successfully retrieved ${localBackupMatches.length} matching records from local backup catalog.`);
    hsSearchCache.set(cacheKey, localBackupMatches);
    return localBackupMatches;
  }

  return [];
}
